import React, { useState, useContext, useMemo } from 'react';
import { AppContext } from '../contexts/AppContext';
import Modal from '../components/Modal';
import { useUI } from '../contexts/UIContext';
import { MedioDePago } from '../types';

const CajaScreen: React.FC = () => {
    const { sesionCaja, openCaja, closeCaja, isReadOnly, pendingOpenVentaId, clearPendingOpenVentaId, openTurnoInTurnos, turnos } = useContext(AppContext);
    const ui = useUI();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [monto, setMonto] = useState('');
    const [expandedVentaId, setExpandedVentaId] = useState<number | null>(null);
    // Defensive: ensure ventas is always an array to avoid runtime errors if backend returns undefined
    const ventas = sesionCaja?.ventas || [];

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setMonto('');
    };

    const handleAction = async () => {
        const montoNum = parseFloat(monto);
        if (isNaN(montoNum) || montoNum < 0) {
            ui.showModal('Error', 'Ingresá un monto válido.');
            return;
        }
        if (sesionCaja && sesionCaja.estado === 'abierta') {
            await closeCaja(montoNum);
        } else {
            await openCaja(montoNum);
        }
        handleCloseModal();
    };
    
    const toggleExpand = (id: number) => {
        setExpandedVentaId(prevId => prevId === id ? null : id);
    };

    // Si otra pantalla solicitó abrir una venta específica, la expandimos
    React.useEffect(() => {
        if (pendingOpenVentaId) {
            setExpandedVentaId(pendingOpenVentaId);
            // limpiar para que no vuelva a abrirse al navegar
            clearPendingOpenVentaId();
        }
    }, [pendingOpenVentaId, clearPendingOpenVentaId]);

    const resumenVentas = useMemo(() => {
        if (!sesionCaja) return null;
        const totalVentas = ventas.reduce((sum, v) => sum + v.total, 0);
        const totalEfectivo = ventas.filter(v => v.medio_de_pago === MedioDePago.EFECTIVO).reduce((sum, v) => sum + v.total, 0);
        const totalTransferencia = ventas.filter(v => v.medio_de_pago === MedioDePago.TRANSFERENCIA).reduce((sum, v) => sum + v.total, 0);
        return { totalVentas, totalEfectivo, totalTransferencia };
    }, [sesionCaja, ventas]);
    
    const sortedVentas = useMemo(() => 
        [...ventas].sort((a,b) => new Date(b.creado_en).getTime() - new Date(a.creado_en).getTime()),
    [ventas]);

    const esperadoEnCaja = sesionCaja && resumenVentas ? sesionCaja.monto_inicial + resumenVentas.totalEfectivo : 0;
    const diferencia = sesionCaja?.monto_final !== undefined && sesionCaja?.monto_final !== null ? sesionCaja.monto_final - esperadoEnCaja : null;

    return (
        <div className="bg-white rounded-lg shadow-md">
            <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">Gestión de Caja</h2>
                    <button 
                        onClick={handleOpenModal} 
                        className={`px-5 py-2 rounded-md text-white font-semibold ${sesionCaja?.estado === 'abierta' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'} disabled:bg-gray-400`}
                        disabled={isReadOnly}
                    >
                        {sesionCaja?.estado === 'abierta' ? 'Cerrar Caja' : 'Abrir Caja'}
                    </button>
                </div>

                {sesionCaja ? (
                    <div>
                        <div className={`p-4 rounded-lg mb-6 ${sesionCaja.estado === 'abierta' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                            <p className="font-bold text-lg">Estado de la Caja: <span className="capitalize">{sesionCaja.estado}</span></p>
                            <p className="text-sm">Responsable Apertura: {sesionCaja.abierta_por}</p>
                            <p className="text-sm">Fecha Apertura: {new Date(sesionCaja.abierta_en).toLocaleString('es-AR')}</p>
                            {sesionCaja.estado === 'cerrada' && sesionCaja.cerrada_por && sesionCaja.cerrada_en && (
                                <>
                                    <p className="text-sm">Responsable Cierre: {sesionCaja.cerrada_por}</p>
                                    <p className="text-sm">Fecha Cierre: {new Date(sesionCaja.cerrada_en).toLocaleString('es-AR')}</p>
                                </>
                            )}
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-600">Monto Inicial</h3>
                                <p className="text-2xl font-bold text-gray-900">${sesionCaja.monto_inicial.toLocaleString('es-AR')}</p>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-600">Ventas en Efectivo</h3>
                                <p className="text-2xl font-bold text-gray-900">${(resumenVentas?.totalEfectivo || 0).toLocaleString('es-AR')}</p>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-600">Total Esperado (Efectivo)</h3>
                                <p className="text-2xl font-bold text-gray-900">${esperadoEnCaja.toLocaleString('es-AR')}</p>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-600">Ventas con Transferencia</h3>
                                <p className="text-2xl font-bold text-gray-900">${(resumenVentas?.totalTransferencia || 0).toLocaleString('es-AR')}</p>
                            </div>
                            {sesionCaja.estado === 'cerrada' && (
                                <>
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <h3 className="font-semibold text-gray-600">Monto Final Declarado</h3>
                                        <p className="text-2xl font-bold text-gray-900">${(sesionCaja.monto_final || 0).toLocaleString('es-AR')}</p>
                                    </div>
                                    <div className={`p-4 rounded-lg ${diferencia === 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                                        <h3 className={`font-semibold ${diferencia === 0 ? 'text-green-800' : 'text-red-800'}`}>Diferencia</h3>
                                        <p className={`text-2xl font-bold ${diferencia === 0 ? 'text-green-800' : 'text-red-800'}`}>${(diferencia || 0).toLocaleString('es-AR')}</p>
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="mt-6">
                            <h3 className="text-xl font-semibold mb-2 text-gray-800">Ventas del Día ({ventas.length})</h3>
                        </div>
                    </div>
                ) : (
                    <p className="text-center text-gray-600">No hay información de caja disponible. Hacé click en "Abrir Caja" para comenzar.</p>
                )}
            </div>

            {sesionCaja && (
                <div className="border-t border-gray-200">
                    {/* Desktop Table View */}
                    <div className="hidden md:block">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hora</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Socio</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Medio de Pago</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cajero</th>
                                    <th scope="col" className="relative px-6 py-3"><span className="sr-only">Detalles</span></th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {sortedVentas.map(v => {
                                    const socioNombre = v.socio ? `${v.socio.nombre} ${v.socio.apellido || ''}`.trim() : 'Consumidor Final';
                                    const turnoVinculado = v.turno_id ? turnos.find(t => t.id === v.turno_id) : null;
                                    return (
                                    <React.Fragment key={v.id}>
                                        <tr>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">#{v.id}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{new Date(v.creado_en).toLocaleTimeString('es-AR', {hour: '2-digit', minute:'2-digit'})}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{socioNombre}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">${v.total.toLocaleString('es-AR')}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{v.medio_de_pago}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{v.creado_por}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    {v.turno_id && (
                                                        <button
                                                            onClick={() => openTurnoInTurnos(v.turno_id!)}
                                                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                                            title={turnoVinculado ? `Ver turno #${v.turno_id}` : 'Ver turno (puede no existir más)'}
                                                        >
                                                            Ver turno
                                                        </button>
                                                    )}
                                                    <button onClick={() => toggleExpand(v.id)} aria-label="Ver detalles">
                                                        <svg className={`w-5 h-5 text-gray-500 transform transition-transform ${expandedVentaId === v.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        {expandedVentaId === v.id && (
                                            <tr className="bg-gray-50">
                                                <td colSpan={7} className="px-6 py-4">
                                                    <h4 className="font-semibold text-sm text-gray-700 mb-2">Detalle de la Venta:</h4>
                                                    <div className="space-y-1">
                                                        {v.items.map(item => (
                                                            <div key={item.producto.id} className="flex justify-between text-sm text-gray-600">
                                                                <span>{item.cantidad} x {item.producto.nombre}</span>
                                                                <span>${(item.cantidad * item.precio_unitario).toLocaleString('es-AR')}</span>
                                                            </div>
                                                        ))}
                                                        {
                                                            // Mostrar resumen de totales antes/después del canje si aplica
                                                        }
                                                        <div className="flex justify-between text-sm text-gray-700 font-medium mt-2">
                                                            <span>Total antes del canje</span>
                                                            <span>${v.items.reduce((s: number, it: any) => s + (it.cantidad * it.precio_unitario), 0).toLocaleString('es-AR')}</span>
                                                        </div>
                                                        {v.puntos_canjeados > 0 && (
                                                            <div className="flex justify-between text-sm text-red-600">
                                                                <span>Puntos canjeados</span>
                                                                <span>-${v.puntos_canjeados.toLocaleString('es-AR')}</span>
                                                            </div>
                                                        )}
                                                        <div className="flex justify-between text-sm text-gray-900 font-bold">
                                                            <span>Total final</span>
                                                            <span>${v.total.toLocaleString('es-AR')}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Accordion View */}
                    <div className="divide-y divide-gray-200 md:hidden">
                        {sortedVentas.map(venta => {
                            const isExpanded = expandedVentaId === venta.id;
                            return (
                                <div key={venta.id} className="p-4">
                                    <button onClick={() => toggleExpand(venta.id)} className="w-full text-left">
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <p className="font-semibold text-gray-800">Venta #{venta.id} - <span className="text-blue-600">${venta.total.toLocaleString('es-AR')}</span></p>
                                                <p className="text-sm text-gray-500">{venta.socio ? `${venta.socio.nombre} ${venta.socio.apellido || ''}`.trim() : 'Consumidor Final'}</p>
                                                {venta.turno_id && (
                                                    <button
                                                        onClick={e => { e.stopPropagation(); openTurnoInTurnos(venta.turno_id!); }}
                                                        className="mt-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                                                    >
                                                        Ver turno →
                                                    </button>
                                                )}
                                            </div>
                                            <svg className={`w-5 h-5 text-gray-500 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                        </div>
                                    </button>
                                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-96 mt-4' : 'max-h-0'}`}>
                                        <div className="space-y-3 pt-2 border-t mt-2">
                                            <div className="flex justify-between">
                                                <span className="text-gray-500">Hora</span>
                                                <span className="font-medium text-gray-800">{new Date(venta.creado_en).toLocaleTimeString('es-AR', {hour: '2-digit', minute:'2-digit'})}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-500">Medio de Pago</span>
                                                <span className="font-medium text-gray-800">{venta.medio_de_pago}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-500">Cajero</span>
                                                <span className="font-medium text-gray-800">{venta.creado_por}</span>
                                            </div>
                                            <div className="pt-2">
                                                <p className="text-sm font-medium text-gray-600">Items:</p>
                                                <ul className="list-disc list-inside text-sm text-gray-600 mt-1 pl-2">
                                                    {(venta.items || []).map(item => (
                                                        <li key={item.producto?.id || item.producto_id}>
                                                            {item.cantidad} x {item.producto?.nombre || 'Producto'} - ${(item.cantidad * item.precio_unitario).toLocaleString('es-AR')}
                                                        </li>
                                                    ))}
                                                </ul>
                                                <div className="mt-2">
                                                    <div className="flex justify-between text-sm text-gray-700 font-medium mt-2">
                                                        <span>Total antes del canje</span>
                                                        <span>${(venta.items || []).reduce((s: number, it: any) => s + (it.cantidad * it.precio_unitario), 0).toLocaleString('es-AR')}</span>
                                                    </div>
                                                    {venta.puntos_canjeados > 0 && (
                                                        <div className="flex justify-between text-sm text-red-600">
                                                            <span>Puntos canjeados</span>
                                                            <span>-${venta.puntos_canjeados.toLocaleString('es-AR')}</span>
                                                        </div>
                                                    )}
                                                    <div className="flex justify-between text-sm text-gray-900 font-bold">
                                                        <span>Total final</span>
                                                        <span>${venta.total.toLocaleString('es-AR')}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    
                    {sortedVentas.length === 0 && (
                        <div className="px-4 md:px-6 py-6 text-center text-gray-500">
                           No hay ventas registradas en esta sesión.
                        </div>
                    )}
                </div>
            )}
            
            <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={sesionCaja?.estado === 'abierta' ? 'Cerrar Caja' : 'Abrir Caja'}>
                <div className="space-y-4">
                    <label className="block text-sm font-medium text-gray-700">
                        {sesionCaja?.estado === 'abierta' ? 'Ingresá el monto final en efectivo:' : 'Ingresá el monto inicial:'}
                    </label>
                    <input
                        type="number"
                        value={monto}
                        onChange={(e) => setMonto(e.target.value)}
                        className="w-full p-2 border rounded-md"
                        placeholder="0.00"
                        autoFocus
                    />
                    <button onClick={handleAction} className="w-full bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700">Confirmar</button>
                </div>
            </Modal>
        </div>
    );
};

export default CajaScreen;